import { useEffect } from 'react'
import type { FC, PropsWithChildren } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useDispatch } from 'react-redux'
import { datadogRum } from '@datadog/browser-rum'
import { datadogLogs } from '@datadog/browser-logs'

import { auth0Authenticate, auth0Deauthenticate } from '~/app/auth'

/**
 * Exporting a component that can live as the direct child of Auth0Provider
 * that can host side effects from authentication state changes
 *
 * This is a convenience component, since authentication state isn't
 * surfaced with the Auth0Provider component itself
 */
export const Auth0Effects: FC<PropsWithChildren> = ({ children }) => {
  const auth0 = useAuth0()
  const dispatch = useDispatch()

  // Depending on the email rather than the user object: it is all this effect reads, and a
  // primitive cannot re-trigger the dispatches below on a new object identity alone.
  const userEmail = auth0.user?.email

  useEffect(() => {
    if (auth0.isAuthenticated) {
      if (userEmail) {
        datadogLogs.setUserProperty('email', userEmail)
        datadogRum.setUserProperty('email', userEmail)
      }
      dispatch(auth0Authenticate())
    } else {
      dispatch(auth0Deauthenticate())
    }
  }, [auth0.isAuthenticated, userEmail, dispatch])

  return <>{children}</>
}
